import type { OrderStatus, ShippingMode } from '@ambernord/shared';

/**
 * Frontend DTO tipi `/supply-orders/:id` lapai.
 * §7 KRITISKI: piegādātāja sesijā API atgriež TIKAI anonimizēto projekciju
 * (`projectSupplyOrderForSupplier`) — visi lauki šeit ir fakultatīvi, un lapa
 * renderē TIEŠI to, ko API atdod, neko nepieprasot no /api/orders.
 */

export interface AddressProjection {
  line1?: string;
  line2?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  contactName?: string;
  contactPhone?: string;
}

export interface ConsigneeDto extends AddressProjection {
  name?: string;
}

export interface SupplyOrderDto {
  id: string;
  ref: string;
  status: OrderStatus;
  terms?: { productId?: string; qty?: number; unit?: string; notes?: string } | null;
  shippingMode?: ShippingMode | null;
  shipToProjection?: AddressProjection | null;
  consignee?: ConsigneeDto | null;
  routeOrigin?: string | null;
  routeDestination?: string | null;
  carrierTenantId?: string | null;
  // Pārvadātāja laikspiedoli nāk tikai ar pilno rindu (platforma) — projekcijā to nav.
  carrierAssignedAt?: string | null;
  carrierConfirmedAt?: string | null;
  deliveryWindowStart?: string | null;
  deliveryWindowEnd?: string | null;
  confirmationDeadline?: string | null;
  confirmedDeliveryDate?: string | null;
  // Per-step laikspiedoli. Piegādātāja projekcija daļu nesatur (confirmedAt/invoicedAt/paidAt).
  confirmedAt?: string | null;
  labelledAt?: string | null;
  loadedAt?: string | null;
  dispatchedAt?: string | null;
  deliveredAt?: string | null;
  inspectedAt?: string | null;
  invoicedAt?: string | null;
  paidAt?: string | null;
}

/** Artefaktu veidi — spoguļo apps/api/src/services/lifecycle-gates.ts ARTIFACT_KINDS. */
export const ARTIFACT_KINDS = [
  'label',
  'cmr',
  'packing_list',
  'delivery_note',
  'driver_signed_cmr',
  'receiver_signed_cmr',
] as const;
export type ArtifactKind = (typeof ARTIFACT_KINDS)[number];

export interface ArtifactsDto {
  status?: OrderStatus;
  warning?: boolean;
  missing?: ArtifactKind[];
  missingScans?: ArtifactKind[];
  error?: string;
}

/** `documents` rinda no GET /api/shipping-docs/supply-order/:id — veidu nes R2 atslēgas prefikss. */
export interface ShippingDocItem {
  id: string;
  fileR2Key: string;
  createdAt?: string | null;
}

export interface ShipmentDto {
  packageCount?: number | null;
  packageType?: string | null;
  netWeightKg?: string | null;
  grossWeightKg?: string | null;
  driverName?: string | null;
  vehiclePlate?: string | null;
  driverSignedAt?: string | null;
  receiverSignedAt?: string | null;
}

export interface PackageLineDto {
  id: string;
  productId?: string | null;
  packageType?: string | null;
  count?: number | null;
  netKg?: string | null;
  grossKg?: string | null;
}

export interface PackingDto {
  shipment: ShipmentDto | null;
  packages: PackageLineDto[];
}

export type ActorKind = 'platform' | 'supplier' | 'customer' | 'carrier' | 'system';

/** Lomas prefikss → aktora veids (saskaņots ar API supply-orders.ts `actorKind`). */
export function actorKindOf(role: string): ActorKind {
  if (role.startsWith('platform_')) return 'platform';
  if (role.startsWith('supplier_')) return 'supplier';
  if (role.startsWith('customer_')) return 'customer';
  if (role.startsWith('carrier_')) return 'carrier';
  return 'system';
}
